import React, { useState } from 'react';
import { FileText, Download, ExternalLink, Calendar, Users, BookOpen, Star, Shield, Target, Zap, ArrowLeft, ChevronRight } from 'lucide-react';

interface WhitepaperProps {
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: string) => void;
}

export default function WhitepaperPage({ onLaunchPlatform, onNavigateToPage }: WhitepaperProps) {
  const [currentView, setCurrentView] = useState<string>('main');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const sections = [
  { 
    id: 'tokenomics', 
      title: 'Tokenomics Model',
      subtitle: '$EKKO Economics',
      icon: <Zap className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: '$EKKO Token',
        subtitle: 'The native utility token powering the entire EKKO v2 ecosystem, designed to align incentives across users, liquidity providers, and the platform.',
        description: 'EKKO is a trading platform built for traders, investors, and enthusiasts who want real-time insights, tracking tools, and AI-powered trading utilities. Our tokenomics ensure long-term growth, sustainability, and trust while offering unique benefits to $EKKO holders.',
        sections: [
          {
            title: 'Token Overview',
            content: [
              'Token Name: EKKO',
              'Token Symbol: $EKKO',
              'Blockchain: Solana',
              'Token Standard: SPL',
              'Total Supply: 1,000,000,000 EKKO',
              'Vesting Period: 2 months',
              'Token Contract: 89S9RdgynPq5odSRmcCDAzg26iYuRw4wqUmzMbjUpump'
            ]
          },
          {
            title: 'Token Distribution',
            content: [
              'Community Ownership: 96.5%',
              'Development: 2%',
              'Marketing: 1%',
              'Team: 0.5%',
              '',
              'Note: Development, marketing, and team allocations are subject to a 2-month vesting period.'
            ]
          },
          {
            title: 'Trading Benefits',
            content: [
              'Holding EKKO tokens provides tangible benefits when trading on the platform, with a tiered system based on holdings. Early holders receive lifetime access to tracking features.',
              '',
              '$EKKO Holder = Zero Fees',
              'Priority order execution',
              'Advanced trading tools access'
            ]
          },
          {
            title: 'Governance',
            content: [
              'EKKO token holders can participate in governance decisions through voting on protocol upgrades, parameter changes, and treasury allocations.',
              '',
              '1 EKKO = 1 vote (25,000 minimum)',
              'Vote delegation available',
              'Transparent proposal system'
            ]
          }
        ]
      }
    },
    {
      id: 'access-tiers',
      title: 'Access Tiers',
      subtitle: 'Platform Access Levels',
      icon: <Star className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'Access Tiers',
        subtitle: 'Unlock Premium Features with $EKKO Token Holdings',
        description: 'Experience tiered access to advanced trading tools and features. Each tier unlocks powerful capabilities designed to enhance your trading performance and provide competitive advantages in the fast-moving DeFi market.',
        sections: [
          {
            title: '',
            content: [
              '25,000 $EKKO per month',
              '',
              'Core Features:',
              '• Rug Tracker - Detect suspicious projects instantly',
              '• Whale Tracker - Monitor large transactions in real-time', 
              '• Custom Filters - Create personalized trading alerts',
              '• Auto Trader - Automated execution with preset rules',
              '• Jupiter Swap Integration - Best routing across all DEXs',
              '• MEV Protection - Shield against front-running attacks',
              '• Anti-Rug Bot - Advanced protection algorithms',
              '• Leaderboards - Track top performers and strategies',
              '• Bubblemaps - Visual token ecosystem analysis'
            ]
          },
          {
            title: '', 
            content: [
              '79,000 $EKKO per month',
              '',
              'Everything in TIER 1 PLUS:',
              '• Sniper Bot - Lightning-fast token launch detection',
              '• Rule-Based Bot - Complex conditional trading logic',
              '• X-Tracker - Social sentiment and influence monitoring',
              '• Copy Trader - Replicate successful trader strategies',
              '• Custom Alerts - Advanced notification system',
              '• Multi-DEX Routes - Optimize across multiple exchanges',
              '• Copy Trade Pools - Join community trading strategies',
              '• Priority Stop Loss - Enhanced risk management tools'
            ]
          },
          {
            title: '',
            content: [
              '150,000 $EKKO per month',
              '',
              'Everything in TIER 2 PLUS:',
              '• EKKO Elite Status - Exclusive platform privileges',
              '• Private Nodes - Dedicated infrastructure access',
              '• Liquidity Sniper - Advanced pool launch detection',
              '• xWallet Trading - Cross-wallet portfolio management',
              '• Auto Copy Trader - Fully automated strategy replication',
              '• Premium Feed - Exclusive market intelligence',
              '• Enhanced Alerts - Priority notification system',
              '• White-glove Support - Dedicated account management'
            ]
          }
        ]
      }
    },
    {
      id: 'architecture',
      title: 'Technical Architecture',
      subtitle: 'Infrastructure & Design',
      icon: <Target className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'Technical Architecture',
        subtitle: 'High-Performance Infrastructure & Design',
        description: 'EKKO is built on a cutting-edge technical foundation designed for high-performance trading on Solana, utilizing enterprise-grade infrastructure and innovative architectural patterns.',
        sections: [
          {
            title: 'Blockchain Infrastructure',
    content: [
              'Solana blockchain integration for sub-second transaction finality and minimal fees',
              'QuickNode infrastructure providing enterprise-grade node connectivity and 99.99% uptime',
              'Direct RPC connections for maximum speed and reliability',
              'Load-balanced endpoints across multiple geographic regions',
              'Redundant node architecture ensuring zero downtime'
            ]
          },
          {
            title: 'System Architecture',
    content: [
              'Microservices architecture enabling horizontal scaling and fault tolerance',
              'Real-time WebSocket connections for instantaneous market data and order execution',
              'Event-driven architecture with message queues for reliable processing',
              'Container orchestration with Kubernetes for auto-scaling capabilities',
              'Service mesh implementation for secure inter-service communication'
            ]
          },
          {
            title: 'Performance Optimization',
    content: [
              'Advanced caching layers with Redis for lightning-fast data retrieval',
              'Load-balanced API endpoints handling 10,000+ requests per second',
              'CDN integration for global content delivery and reduced latency',
              'Database sharding and replication for optimal read/write performance',
              'Memory pooling and garbage collection optimization'
            ]
          },
          {
            title: 'Monitoring & Operations',
    content: [
              'Comprehensive monitoring and alerting systems for system health tracking',
              'Auto-scaling infrastructure adapting to trading volume fluctuations',
              'Distributed tracing for performance analysis and debugging',
              '24/7 operational monitoring with automated incident response',
              'Performance metrics dashboard with real-time system analytics'
            ]
          }
        ]
      }
  },
  ];

  const handleSectionClick = (sectionId: string) => {
    // If tokenomics is clicked, navigate to the dedicated tokenomics page
    if (sectionId === 'tokenomics') {
      onNavigateToPage?.('tokenomics');
      return;
    }
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView(sectionId);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackClick = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('main');
      setIsTransitioning(false);
    }, 300);
  };

  const getCurrentSection = () => sections.find(s => s.id === currentView);
  
  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background Hero Image - Fixed to bottom */}
      <div className="fixed bottom-0 right-0 w-full h-full pointer-events-none z-1">
        <div className="relative h-full flex items-end justify-end">
          <div className="relative">
            <img 
              src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/214.png"
              alt="EKKO Whitepaper Technical Documentation"
              className="h-full w-auto object-contain object-bottom"
              style={{ 
                height: '100vh',
                maxWidth: '60vw'
              }}
            />
            {/* Left fade gradient */}
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent"></div>
            {/* Right fade gradient */}
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute top-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Background Text - CENTERED AND STRAIGHT */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[12vw] opacity-20 leading-none">WHITEPAPER</div>
          <div className="text-[10vw] opacity-15 leading-none">VISION</div>
          <div className="text-[8vw] opacity-10 leading-none">FUTURE</div>
        </div>
      </div>
      
      {/* Main Content - Transitioning Views */}
      <section className="relative z-10 min-h-screen flex items-center py-20">
        <div className="container mx-auto px-4">
          
          {/* Main Overview */}
          {currentView === 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <div className="max-w-2xl mx-auto lg:mx-0 lg:max-w-3xl">
                {/* Content */}
                <div className="text-center lg:text-left space-y-8">
                  {/* Title */}
                  <div className="space-y-6">
                    <h1 className="text-6xl lg:text-7xl font-black leading-tight">
                      <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                        WHITEPAPER
                      </span>
                    </h1>
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                      Technical Vision &
                      <br />
                      <span className="text-[#24c35b]">Strategic Roadmap</span>
                    </h2>
            </div>
            
                  {/* Description */}
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Discover our comprehensive technical documentation, tokenomics, and strategic vision for revolutionizing DeFi trading on Solana.
                  </p>
                  
                  {/* Clickable Key Sections */}
                  <div className="grid grid-cols-1 gap-4 max-w-lg mx-auto lg:mx-0">
                    {sections.map((section) => (
                <button 
                        key={section.id}
                        onClick={() => handleSectionClick(section.id)}
                        className="w-full glassmorphism p-4 rounded-xl border border-white/20 flex items-center gap-4 hover:bg-gray-50 transition-all duration-300 group"
                      >
                        <div className="w-10 h-10 bg-[#24c35b]/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          {section.icon}
                    </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900">{section.title}</div>
                          <div className="text-sm text-gray-600">{section.subtitle}</div>
                  </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </button>
              ))}
            </div>
            
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto lg:mx-0">
                    <button 
                      onClick={onLaunchPlatform}
                      className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group"
                    >
                      <Download className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      <span>Launch Platform</span>
                    </button>
          </div>
                </div>
              </div>
                </div>
              )}
              
          {/* Detail Views */}
          {currentView !== 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {(() => {
                const section = getCurrentSection();
                if (!section) return null;

                return (
                  <div className="max-w-6xl mx-auto">
                    {/* Back Button */}
                    <button
                      onClick={handleBackClick}
                      className="mb-8 flex items-center gap-3 glassmorphism px-6 py-3 rounded-xl border border-white/20 hover:bg-gray-50 transition-colors group"
                    >
                      <ArrowLeft className="w-4 h-4 text-[#24c35b] group-hover:-translate-x-1 transition-transform" />
                      <span className="font-medium text-gray-900">Back to Whitepaper</span>
                    </button>

                    {/* Section Header */}
                    <div className="text-center mb-16">
                      <div className="flex items-center justify-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-[#24c35b]/10 rounded-2xl flex items-center justify-center">
                          {section.icon}
                        </div>
                        <div className="text-left">
                          <h1 className="text-4xl lg:text-5xl font-black text-gray-900">{section.details.title}</h1>
                          <p className="text-xl text-[#24c35b] font-medium">{section.details.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                        {section.details.description}
                      </p>
                </div>
                
                    {/* Content Sections - Special styling for Access Tiers */}
                    {section.id === 'access-tiers' ? (
                      <div className="space-y-8">
                        {/* Tier Cards */}
                        <div className="grid lg:grid-cols-3 gap-8">
                          {section.details.sections.slice(0, 3).map((tierSection, index) => {
                            const tierNames = ['TIER 1', 'TIER 2', 'ELITE'];
                            
                            return (
                              <div key={index} className="relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group">
                                {/* Architectural Corner Decorations */}
                                <div className="absolute top-0 left-0 w-8 h-8">
                                  <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2"></div>
                                  <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2"></div>
                                </div>
                                <div className="absolute top-0 right-0 w-8 h-8">
                                  <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2"></div>
                                  <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2"></div>
                                </div>
                                
                                {/* Tier Badge */}
                                <div className="absolute top-4 right-4">
                                  <div className="px-3 py-1 bg-[#24c35b]/10 border border-[#24c35b]/30 rounded text-xs font-bold text-[#24c35b]">
                                    {tierNames[index]}
                                  </div>
                                </div>
                                
                                {/* Accent Line */}
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#24c35b]"></div>
                                
                                <div className="p-8">
                                  <h3 className="text-2xl font-black text-gray-900 mb-6">{tierSection.title}</h3>
                                  <div className="space-y-3">
                                    {tierSection.content.map((item, itemIndex) => {
                                      if (item === '') return <div key={itemIndex} className="h-2"></div>;
                                      if (item.includes('$EKKO per month')) return (
                                        <div key={itemIndex} className="mb-6">
                                          <div className="text-4xl font-black text-[#24c35b] leading-tight">
                                            {item.split(' ')[0]}
                                          </div>
                                          <div className="text-lg font-bold text-gray-900 mt-1">
                                            $EKKO per month
                                          </div>
                                        </div>
                                      );
                                      if (item.includes('Core Features:') || item.includes('Everything in TIER')) return (
                                        <div key={itemIndex} className="text-lg font-semibold text-gray-900 mt-6 mb-4 border-b border-gray-200 pb-2">
                                          {item}
                                        </div>
                                      );
                                      return (
                                        <div key={itemIndex} className="flex items-start gap-3">
                                          <div className="w-1 h-1 bg-[#24c35b] rounded-full mt-2 flex-shrink-0"></div>
                                          <span className="text-gray-600 leading-relaxed text-sm">{item}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Bottom Architectural Line */}
                                <div className="absolute bottom-0 left-8 right-8 h-0.5 bg-gray-200"></div>
                                
                                {/* Bottom Corner Decorations */}
                                <div className="absolute bottom-0 left-0 w-8 h-8">
                                  <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2"></div>
                                  <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2"></div>
                                </div>
                                <div className="absolute bottom-0 right-0 w-8 h-8">
                                  <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2"></div>
                                  <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2"></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      /* Regular Content Sections for other pages */
                      <div className="grid md:grid-cols-2 gap-8">
                        {section.details.sections.map((contentSection, index) => (
                          <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">{contentSection.title}</h3>
                            <div className="space-y-4">
                              {contentSection.content.map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-start gap-3">
                                  <div className="w-1.5 h-1.5 bg-[#24c35b] rounded-full mt-2 flex-shrink-0"></div>
                                  <span className="text-gray-600 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Document Info Section - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="glassmorphism px-8 py-4 rounded-xl border border-white/20 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">Version 1.0 - March 2025</span>
                </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">EKKO Development Team</span>
                </div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">~15 min read</span>
          </div>
        </div>
      </div>
    </div>
  );
} 